import React, { useState, useEffect } from 'react';
import { piecesRequisesAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  FileText,
  Building2,
  Tractor,
  HardHat,
  Construction,
  MoreHorizontal,
} from 'lucide-react';

const CLIENT_CATEGORIES = [
  { id: 'agriculteur', label: 'Agriculteur', icon: Tractor, color: 'bg-green-100 text-green-700' },
  { id: 'btp', label: 'BTP', icon: HardHat, color: 'bg-orange-100 text-orange-700' },
  { id: 'vrd', label: 'VRD', icon: Construction, color: 'bg-blue-100 text-blue-700' },
  { id: 'autres', label: 'Autres', icon: MoreHorizontal, color: 'bg-gray-100 text-gray-700' },
];

const CLIENT_TYPES = [
  { id: 'particulier', label: 'Particulier', color: 'bg-purple-100 text-purple-700' },
  { id: 'professionnel', label: 'Professionnel', color: 'bg-indigo-100 text-indigo-700' },
  { id: 'collectivite', label: 'Collectivité', color: 'bg-teal-100 text-teal-700' },
];

const PiecesRequisesPage = () => {
  const [pieces, setPieces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPiece, setEditingPiece] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    description: '',
    categories: [],
    client_types: [],
    for_financement: false,
    for_snc: false,
    order: 0,
  });

  useEffect(() => {
    fetchPieces();
  }, []);

  const fetchPieces = async () => {
    try {
      const response = await piecesRequisesAPI.getAll();
      setPieces(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des pièces');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Le nom de la pièce est requis');
      return;
    }
    
    setFormLoading(true);
    try {
      if (editingPiece) {
        await piecesRequisesAPI.update(editingPiece.id, form);
        toast.success('Pièce mise à jour');
      } else {
        await piecesRequisesAPI.create(form);
        toast.success('Pièce créée');
      }
      setDialogOpen(false);
      resetForm();
      fetchPieces();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (pieceId) => {
    if (!window.confirm('Supprimer cette pièce ?')) return;
    
    try {
      await piecesRequisesAPI.delete(pieceId);
      toast.success('Pièce supprimée');
      fetchPieces();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEditDialog = (piece) => {
    setEditingPiece(piece);
    setForm({
      name: piece.name,
      description: piece.description || '',
      categories: piece.categories || [],
      client_types: piece.client_types || [],
      for_financement: piece.for_financement || false,
      for_snc: piece.for_snc || false,
      order: piece.order || 0,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPiece(null);
    setForm({
      name: '',
      description: '',
      categories: [],
      client_types: [],
      for_financement: false,
      for_snc: false,
      order: 0,
    });
  };

  const toggleCategory = (categoryId) => {
    if (form.categories.includes(categoryId)) {
      setForm({ ...form, categories: form.categories.filter(c => c !== categoryId) });
    } else {
      setForm({ ...form, categories: [...form.categories, categoryId] });
    }
  };

  const toggleClientType = (typeId) => {
    if (form.client_types.includes(typeId)) {
      setForm({ ...form, client_types: form.client_types.filter(t => t !== typeId) });
    } else {
      setForm({ ...form, client_types: [...form.client_types, typeId] });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pieces-requises-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
            Pièces requises
          </h1>
          <p className="text-slate-500 mt-1">
            Documents à fournir par catégorie de client pour les demandes de financement/SNC
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-yellow-500 hover:bg-yellow-600 text-slate-900" data-testid="add-piece-btn">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter une pièce
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
                {editingPiece ? 'Modifier la pièce' : 'Ajouter une pièce'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nom de la pièce *</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex: Carte d'identité, Kbis, RIB..."
                  required
                  data-testid="piece-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Détails supplémentaires..."
                  rows={2}
                  data-testid="piece-description-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Catégories concernées</Label>
                <div className="grid grid-cols-2 gap-2">
                  {CLIENT_CATEGORIES.map((cat) => {
                    const Icon = cat.icon;
                    const isSelected = form.categories.includes(cat.id);
                    return (
                      <div
                        key={cat.id}
                        onClick={() => toggleCategory(cat.id)}
                        className={`flex items-center gap-2 p-2 border rounded-md cursor-pointer transition-colors ${
                          isSelected ? 'border-yellow-500 bg-yellow-50' : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`category-${cat.id}`}
                      >
                        <Checkbox checked={isSelected} />
                        <Icon className="w-4 h-4" />
                        <span className="text-sm">{cat.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="space-y-3 pt-2 border-t">
                <Label>Types de client concernés</Label>
                <p className="text-xs text-slate-500">Cochez les types de clients pour lesquels cette pièce est requise. Si aucun n'est coché, la pièce sera demandée à tous.</p>
                <div className="flex flex-wrap gap-2">
                  {CLIENT_TYPES.map((type) => (
                    <div
                      key={type.id}
                      className={`flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer transition-colors ${
                        form.client_types.includes(type.id) 
                          ? `border-indigo-500 ${type.color}` 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => toggleClientType(type.id)}
                    >
                      <Checkbox checked={form.client_types.includes(type.id)} />
                      <span className="text-sm">{type.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3 pt-2 border-t">
                <Label>Requis pour :</Label>
                <div className="flex gap-4">
                  <div 
                    className={`flex items-center gap-2 p-3 border rounded-md cursor-pointer transition-colors ${
                      form.for_financement ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => setForm({ ...form, for_financement: !form.for_financement })}
                    data-testid="for-financement-checkbox"
                  >
                    <Checkbox checked={form.for_financement} />
                    <Building2 className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium">Financement</span>
                  </div>
                  <div 
                    className={`flex items-center gap-2 p-3 border rounded-md cursor-pointer transition-colors ${
                      form.for_snc ? 'border-yellow-500 bg-yellow-50' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => setForm({ ...form, for_snc: !form.for_snc })}
                    data-testid="for-snc-checkbox"
                  >
                    <Checkbox checked={form.for_snc} />
                    <FileText className="w-4 h-4 text-yellow-600" />
                    <span className="text-sm font-medium">SNC</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2 pt-2">
                <Label htmlFor="order">Ordre d'affichage</Label>
                <Input
                  id="order"
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
                  min="0"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="bg-yellow-500 hover:bg-yellow-600 text-slate-900"
                  disabled={formLoading || !form.name.trim()}
                  data-testid="save-piece-btn"
                >
                  {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingPiece ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <p className="text-sm text-blue-800">
            <strong>Comment ça marche :</strong> Ces pièces seront automatiquement incluses dans les emails 
            de devis selon le type de client (particulier, professionnel, collectivité) et les options sélectionnées (financement, SNC).
            Lors de l'envoi d'email, cochez "Inclure la liste des pièces" pour ajouter cette liste au message.
          </p>
        </CardContent>
      </Card>

      {/* Pieces Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {pieces.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Aucune pièce configurée</p>
              <p className="text-sm text-slate-400 mt-1">
                Ajoutez des pièces requises pour les demandes de financement
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pièce</TableHead>
                  <TableHead>Types de client</TableHead>
                  <TableHead>Catégories</TableHead>
                  <TableHead>Options</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pieces.map((piece) => (
                  <TableRow key={piece.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-800">{piece.name}</p>
                        {piece.description && (
                          <p className="text-sm text-slate-500 line-clamp-1">{piece.description}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {piece.client_types?.map((typeId) => {
                          const type = CLIENT_TYPES.find(t => t.id === typeId);
                          if (!type) return null;
                          return (
                            <Badge key={typeId} className={type.color}>
                              {type.label}
                            </Badge>
                          );
                        })}
                        {(!piece.client_types || piece.client_types.length === 0) && (
                          <span className="text-slate-400 text-sm">Tous</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {piece.categories?.map((catId) => {
                          const cat = CLIENT_CATEGORIES.find(c => c.id === catId);
                          if (!cat) return null;
                          return (
                            <Badge key={catId} className={cat.color}>
                              {cat.label}
                            </Badge>
                          );
                        })}
                        {(!piece.categories || piece.categories.length === 0) && (
                          <span className="text-slate-400 text-sm">Toutes</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {piece.for_financement && (
                          <Badge className="bg-blue-100 text-blue-700">Financement</Badge>
                        )}
                        {piece.for_snc && (
                          <Badge className="bg-yellow-100 text-yellow-700">SNC</Badge>
                        )}
                        {!piece.for_financement && !piece.for_snc && (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(piece)}
                          data-testid={`edit-piece-${piece.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(piece.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-piece-${piece.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PiecesRequisesPage;
